import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * React Query hooks for the Phase C C3 admin triage UI (T14).
 *
 * Server contract: `apps/api/src/routes/admin/cpe-mappings.routes.ts`.
 */

// ─── Server-shape types ──────────────────────────────────────────────────────

export interface CpeMappingsStats {
  totalMapped: number;
  totalUnmapped: number;
  conflictCount: number;
}

export interface UnmappedRow {
  cpeVendor: string;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  sampleCveIds: string[];
}

export interface UnmappedPage {
  page: number;
  pageSize: number;
  rows: UnmappedRow[];
}

export interface ConflictCandidate {
  id: string;
  name: string;
}

export interface ConflictRow {
  cpeVendor: string;
  placeholderVendorId: string;
  mappedAt: string;
  candidates: ConflictCandidate[];
}

export interface CpeMappingsResponse {
  stats: CpeMappingsStats;
  unmapped: UnmappedPage;
  conflicts: ConflictRow[];
}

// ─── Mutation payloads ───────────────────────────────────────────────────────

export interface MapToExistingPayload {
  cpeVendor: string;
  vendorId: string;
  deleteFromUnmapped?: boolean;
}

export interface CreateVendorPayload {
  cpeVendor: string;
  vendorName: string;
  vendorAliases?: string[];
}

export interface ResolveConflictPayload {
  cpeVendor: string;
  chosenVendorId: string;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

const KEY = "admin-cpe-mappings";

export function useCpeMappings(page: number, pageSize: number) {
  return useQuery({
    queryKey: [KEY, page, pageSize],
    queryFn: () =>
      api.get<CpeMappingsResponse>(
        `/admin/cpe-mappings?page=${page}&pageSize=${pageSize}`,
      ),
  });
}

export function useMapToExisting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: MapToExistingPayload) =>
      api.post<{ ok: true; mapping: { vendorId: string; vendorName: string } }>(
        "/admin/cpe-mappings",
        payload,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useCreateVendorMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVendorPayload) =>
      api.post<{ ok: true; vendorId: string }>(
        "/admin/cpe-mappings/create-vendor",
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
}

export function useResolveConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ResolveConflictPayload) =>
      api.post<{ ok: true }>("/admin/cpe-mappings/resolve-conflict", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}
